/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import queryString from "query-string";
import ReactTable from "react-table";

import { Link, useParams, useHistory } from "react-router-dom";
import moment from "moment";

import Approval from "./Approval";

import ApprovalService from "../../services/approvalService";
import Loading from "../common/Loading";

import ProjectSelect from "../common/ProjectSelect";
import CallTypeSelect from "../common/CallTypeSelect";
import ServiceSelect from "../common/ServiceSelect";

const generateApprovalColumns = ({
  handleSelectAll,
  handleSelect,
  selected,
  allSelected
}) => [
  {
    Header: () => (
      <input
        type="checkbox"
        checked={allSelected}
        onChange={() => handleSelectAll(!allSelected)}
      />
    ),
    accessor: "assignedToId",
    id: "assignedToId",
    maxWidth: 45,
    sortable: false,
    filterable: false,
    Cell: ({ value }) => (
      <input
        type="checkbox"
        checked={selected.includes(value)}
        onChange={() => handleSelect(value)}
      />
    )
  },
  {
    Header: "Employee",
    accessor: "assignedTo",
    id: "assignedTo",
    minWidth: 160
  },
  {
    Header: "Calls",
    accessor: "numCalls",
    id: "numCalls",
    maxWidth: 80
  },
  {
    Header: "Flagged",
    accessor: "numFlagged",
    id: "numFlagged",
    maxWidth: 80
  },
  {
    Header: "Total (hrs)",
    accessor: "totalHours",
    id: "totalHours",
    maxWidth: 150
  },
  {
    Header: "Billable (hrs)",
    accessor: "billableHours",
    id: "billableHours",
    maxWidth: 150
  },
  {
    Header: "Non-Billable (hrs)",
    accessor: "nonBillableHours",
    id: "nonBillableHours",
    maxWidth: 150
  }
];

const ApprovalList = ({ data, loading, onRowClick, columns }) => {
  const { assignedToId } = useParams();

  if (loading)
    return (
      <div className="loading">
        <Loading />
      </div>
    );
  return data && data.length ? (
    <div className="approval-container">
      <ReactTable
        className="-highlight"
        data={data}
        defaultSorted={[{ id: "assignedTo", desc: false }]}
        columns={columns}
        showPagination={false}
        pageSize={data.length}
        getTrProps={(state, { original }) => ({
          onClick: () => {
            onRowClick(original.assignedToId);
          },
          style: {
            background:
              original.assignedToId === assignedToId ? "hsla(0, 0%, 95%)" : ""
          }
        })}
      />
    </div>
  ) : (
    <div className="loading">
      <h4 style={{ opacity: 0.15 }}>No Data</h4>
    </div>
  );
};
ApprovalList.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
  columns: PropTypes.array,
  onRowClick: PropTypes.func
};

const ApprovalCall = ({ call }) => (
  <div>
    <div className="approval-call">
      <h4>{call.customer}</h4>
      <h4>{moment(call.completed_date).format("MM/DD/YY")}</h4>
      <ProjectSelect onChange={() => {}} />
      <CallTypeSelect onChange={() => {}} />
      <ServiceSelect onChange={() => {}} />
      <span />
      <div className="text-input">
        <label htmlFor={`actual-hrs-${call._id}`}>Actual</label>
        <input
          type="number"
          id={`actual-hrs-${call._id}`}
          value={call.actual_hrs}
        />
      </div>
      <div className="text-input">
        <label htmlFor={`billable-hrs-${call._id}`}>Billable</label>
        <input
          type="number"
          id={`billable-hrs-${call._id}`}
          value={call.billable_hrs}
        />
      </div>
      <div className="text-input">
        <label htmlFor={`internal-notes-${call._id}`}>Internal Notes</label>
        <textarea
          type="text"
          id={`internal-notes-${call._id}`}
          value={call.internal_notes}
          rows="3"
        />
      </div>
      <div className="text-input">
        <label htmlFor={`billing-description-${call._id}`}>
          Billing Description
        </label>
        <textarea
          type="text"
          id={`billing-description-${call._id}`}
          defaultValue={call.billing_description}
          rows="3"
        />
      </div>
      <div className="text-input review-note">
        <label htmlFor={`review-note-${call._id}`}>Review Note</label>
        <textarea
          type="text"
          id={`review-note-${call._id}`}
          defaultValue={call.review_note}
          rows="3"
        />
      </div>
    </div>
    <pre>
      <code>{JSON.stringify(call, null, 2)}</code>
    </pre>
  </div>
);
ApprovalCall.propTypes = {
  call: PropTypes.object
};

const ApprovalCallList = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="loading">
        <Loading />
      </div>
    );
  }
  return data && data.length ? (
    <div className="approval-call-list">
      {data.map((call) => (
        <ApprovalCall key={call._id} call={call} />
      ))}
    </div>
  ) : (
    <div className="loading">
      <h4 style={{ opacity: 0.15 }}>No Data</h4>
    </div>
  );
};

const ApprovalItem = ({ assignedToId }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const result = await ApprovalService.get(`/manager/${assignedToId}`);
      setData(result);
    };
    try {
      setLoading(true);
      if (assignedToId) {
        fetchData();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, [assignedToId]);

  return <ApprovalCallList data={data} loading={loading} />;
};
const ManagerApproval = () => {
  const [selected, setSelected] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { assignedToId } = useParams();
  const history = useHistory();

  const fetchData = async (filters) => {
    setLoading(true);
    setSelected([]);
    setData([]);
    try {
      const query = queryString.stringify(filters, { arrayFormat: "bracket" });
      const result = await ApprovalService.get(`/manager?${query}`);
      if (result) {
        setData(result);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchData();
  }, []);

  const handleSelect = useCallback(
    (id) => {
      const currSelected = [...selected];
      const currIndex = currSelected.indexOf(id);
      if (currIndex > -1) {
        currSelected.splice(currIndex, 1);
      } else {
        currSelected.push(id);
      }
      setSelected(currSelected);
    },
    [selected]
  );

  const APPROVAL_COLUMNS = useMemo(() => {
    const handleSelectAll = (select = true) => {
      setSelected(select ? data.map((d) => d.assignedToId) : []);
    };
    return generateApprovalColumns({
      handleSelect,
      handleSelectAll,
      selected,
      allSelected: selected.length === data.length
    });
  }, [data, handleSelect, selected]);

  return (
    <Approval fetchData={fetchData}>
      <div className="approval-wrapper">
        <div className="approval-item-container is-open">
          <ApprovalList
            data={data}
            loading={loading}
            onRowClick={(id) => {
              history.push(`/app/approval/manager/${id}`);
            }}
            columns={APPROVAL_COLUMNS}
          />
        </div>
        <div
          className={`approval-item-container ${assignedToId ? "is-open" : ""}`}
        >
          <Link to="/app/approval/manager" className="close-approval-item">
            <button aria-label="Assigned To" className="delete" />
          </Link>
          <ApprovalItem assignedToId={assignedToId} />
        </div>
      </div>
    </Approval>
  );
};

export default ManagerApproval;
